/**
 * LAT-904 Phase 3 — the "open a fresh Claudia thread for this ask-intent" chain.
 *
 * Extracted out of `claudia-panel.tsx`, where it was a 45-line effect that mixed dedup,
 * thread creation, document attachment and the send gate. Two things went wrong there and
 * are the reason this hook exists:
 *
 * - **No generation guard.** The handled-key ref was written before the first `await`, so a
 *   second intent started a second chain while the first was still in flight. Whichever chain
 *   finished last wrote the gate target, and `requestKey` is monotonic, so a stale target
 *   could never match again — Send stayed silently dead for the rest of the session.
 * - **No recovery.** A failed create left the intent pending with the gate shut.
 * - **No escape.** Clearing an intent mid-flight (opening another conversation, or "New
 *   chat") left the handled-key ref pointing at the cleared key, so the abandoned chain
 *   still passed its generation check and adopted the thread the user had just escaped.
 *
 * The guard here is the same ref, re-read after every `await`: a chain that is no longer the
 * handled one does not switch, does not open the gate, and cleans up the blank thread it
 * created. Clearing the intent retires the generation (the ref goes back to `null`), which is
 * safe because `requestKey` is monotonic and can never revisit a handled key. A failed chain
 * clears the intent, reports the error and opens the gate — unless its generation is already
 * dead, in which case the failure is swallowed.
 *
 * The hook owns only the race; the panel owns the Convex calls (`createThread`,
 * `adoptThread`, `cleanupSupersededThread`) so the "New chat" button and this chain share one
 * create-and-track implementation.
 */

import { useEffect, useRef, useState } from "react";

/** The subset of `RightSidebarClaudiaAskIntent` this hook needs. */
export interface AskClaudiaNewThreadIntent {
  requestKey: number;
  startNewThread?: boolean;
}

export interface UseAskClaudiaNewThreadIntentOptions {
  /** The pending ask-intent, or null. Intents without `startNewThread` are ignored. */
  intent: AskClaudiaNewThreadIntent | null;
  /** The thread the surface is currently showing. */
  activeThreadId: string | null;
  /** Creates a fresh thread and resolves with its id. Must NOT switch to it. */
  createThread: () => Promise<string>;
  /** Makes a freshly created thread the active one (switch + analytics + tab). */
  adoptThread: (threadId: string) => void;
  /** Best-effort cleanup of a blank thread created by a superseded chain. */
  cleanupSupersededThread: (threadId: string) => void;
  /** Drops the pending intent (a chain that failed cannot be consumed). */
  clearIntent: () => void;
  /** Surfaces a create failure to the user. */
  onError: (cause: unknown) => void;
}

export interface UseAskClaudiaNewThreadIntentResult {
  /**
   * LAT-620: when an intent opens a fresh thread the composer prefills immediately, but
   * SENDING is blocked until that thread is the active one — otherwise the prompt would
   * submit into (and steer) the prior thread. Intents that do not request a new thread are
   * never blocked, and neither is an intent whose create failed.
   */
  blockSend: boolean;
  /** True only while the create round trip is in flight (a subset of `blockSend`). */
  isPreparing: boolean;
}

export function useAskClaudiaNewThreadIntent({
  intent,
  activeThreadId,
  createThread,
  adoptThread,
  cleanupSupersededThread,
  clearIntent,
  onError,
}: UseAskClaudiaNewThreadIntentOptions): UseAskClaudiaNewThreadIntentResult {
  // Written synchronously the moment a new intent is seen, and re-read after every await:
  // it is both the "create once per intent" dedup key and the generation guard.
  const handledRequestKeyRef = useRef<number | null>(null);
  const [readyTarget, setReadyTarget] = useState<{
    requestKey: number;
    threadId: string;
  } | null>(null);
  const [failedRequestKey, setFailedRequestKey] = useState<number | null>(null);
  const [preparingRequestKey, setPreparingRequestKey] = useState<number | null>(null);

  // Latest-value refs keep the effect keyed on the intent alone: a re-rendered callback
  // identity must not be able to start a second chain for an intent already handled.
  const collaboratorsRef = useRef({
    createThread,
    adoptThread,
    cleanupSupersededThread,
    clearIntent,
    onError,
  });
  collaboratorsRef.current = {
    createThread,
    adoptThread,
    cleanupSupersededThread,
    clearIntent,
    onError,
  };

  const requestKey = intent?.startNewThread ? intent.requestKey : null;

  useEffect(() => {
    if (requestKey === null) {
      // The intent was cleared mid-flight (another conversation opened, or "New chat"):
      // retire the generation so a create still in flight loses its guard check and cleans
      // up instead of dragging the panel back onto the thread the user just escaped.
      // Safe against re-creating an already handled intent: `requestKey` is a monotonic
      // counter, so the next intent's key is strictly greater than any key seen before.
      handledRequestKeyRef.current = null;
      setPreparingRequestKey(null);
      return;
    }
    if (handledRequestKeyRef.current === requestKey) {
      return;
    }
    handledRequestKeyRef.current = requestKey;
    setPreparingRequestKey(requestKey);

    void (async () => {
      const collaborators = collaboratorsRef.current;
      try {
        const createdThreadId = await collaborators.createThread();
        if (handledRequestKeyRef.current !== requestKey) {
          // Superseded while the create was in flight: the newer chain owns the surface, so
          // this thread is a blank orphan. Cleanup is best-effort by contract.
          collaborators.cleanupSupersededThread(createdThreadId);
          return;
        }
        collaborators.adoptThread(createdThreadId);
        setReadyTarget({ requestKey, threadId: createdThreadId });
        setPreparingRequestKey((current) => (current === requestKey ? null : current));
      } catch (cause: unknown) {
        if (handledRequestKeyRef.current !== requestKey) {
          // Superseded or abandoned: the recovery path (clear + toast) belongs to whichever
          // intent is still pending, so a dead generation swallows its failure rather than
          // firing an error toast for a chat nobody is waiting on.
          return;
        }
        // Finding C: the old effect reset the ref "for retry", but nothing re-ran it — the
        // intent stayed pending behind a shut gate. Drop the intent and open the gate.
        setFailedRequestKey(requestKey);
        setPreparingRequestKey((current) => (current === requestKey ? null : current));
        collaborators.clearIntent();
        collaborators.onError(cause);
      }
    })();
  }, [requestKey]);

  const isNewThreadIntentReady =
    requestKey === null ||
    failedRequestKey === requestKey ||
    (readyTarget?.requestKey === requestKey && readyTarget.threadId === activeThreadId);

  return {
    blockSend: requestKey !== null && !isNewThreadIntentReady,
    isPreparing: preparingRequestKey !== null && preparingRequestKey === requestKey,
  };
}
